import { Injectable, inject } from '@angular/core';
import { environment as env } from 'src/environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { StoreService } from '../store-service/store.service';
import { UserService } from '../user-service/user.service';
import {
  Service,
  ServiceSearchKey,
  ServiceSearchKeyOptions,
} from '../../models/service';
import { EntitlementRequest, Entitlement } from '../../models/entitlement';
import { Client } from '../../models/client';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  private http = inject(HttpClient);
  constructor(
    private storeService: StoreService,
    private userService: UserService,
  ) {}

  async getAllServices(): Promise<Service[]> {
    let service: Service[];

    let url = `${env.base_api_url}/service`;
    let token = await this.userService.getAccessToken();
    let headers = new HttpHeaders().set('Authorization', 'Bearer ' + token);

    return await firstValueFrom(
      this.http.get<Service[]>(url, { headers: headers }),
    );
  }

  async initServices() {
    if (this.storeService.services.length == 0) {
      let serviceRawData = await this.getAllServices();
      this.storeService.initServices(serviceRawData);
    }
  }

  async getClients(): Promise<Client[]> {
    let client: Client[];

    let url = `${env.base_api_url}/client`;
    let token = await this.userService.getAccessToken();
    let headers = new HttpHeaders().set('Authorization', 'Bearer ' + token);

    return await firstValueFrom(
      this.http.get<Client[]>(url, { headers: headers }),
    );
  }

  async initClients() {
    if (this.storeService.clients.length == 0) {
      let clientsRawData = await this.getClients();
      this.storeService.initClients(clientsRawData);
    }
  }

  async deleteClient(data: Client) {
    let url = `${env.base_api_url}/client/${data.id}`;
    let token = await this.userService.getAccessToken();
    let headers = new HttpHeaders().set('Authorization', 'Bearer ' + token);

    await this.http.delete<Client>(url, { headers: headers }).toPromise();
    return this.storeService.deleteClient(data);
  }

  async initEntitlementByServiceId(serviceId: string) {
    if (
      this.storeService.entitlementServices.filter(
        (a) => a.serviceId === serviceId,
      ).length == 0
    ) {
      let entitlementRawData = await this.getEntitlementByServiceId(serviceId);
      this.storeService.initEntitlementServices(entitlementRawData);
    }
  }

  async initEntitlementByClientId(clientId: string) {
    if (
      this.storeService.entitlementClients.filter(
        (a) => a.clientId === clientId,
      ).length == 0
    ) {
      let entitlementRawData = await this.getEntitlementByClientId(clientId);
      this.storeService.initEntitlementClients(entitlementRawData);
    }
  }

  async getClientIdpById(): Promise<Client[]> {
    let url = `${env.base_api_url}/idp-client/auth0`;
    let token = await this.userService.getAccessToken();
    let headers = new HttpHeaders().set('Authorization', 'Bearer ' + token);

    return await firstValueFrom(
      this.http.get<Client[]>(url, { headers: headers }),
    );
  }

  async getClientIdpByClientId(clientId: string): Promise<Client> {
    let url = `${env.base_api_url}/idp-client/auth0/${clientId}`;
    let token = await this.userService.getAccessToken();
    let headers = new HttpHeaders().set('Authorization', 'Bearer ' + token);

    return await firstValueFrom(
      this.http.get<Client>(url, { headers: headers }),
    );
  }

  async getOktaClientByClientId(clientId: string): Promise<Client> {
    let url = `${env.base_api_url}/idp-client/okta/${clientId}`;
    let token = await this.userService.getAccessToken();
    let headers = new HttpHeaders().set('Authorization', 'Bearer ' + token);

    return await firstValueFrom(
      this.http.get<Client>(url, { headers: headers }),
    );
  }

  async getClientById(clientId: string): Promise<Client> {
    let url = `${env.base_api_url}/client/${clientId}`;
    let token = await this.userService.getAccessToken();
    let headers = new HttpHeaders().set('Authorization', 'Bearer ' + token);

    return await firstValueFrom(
      this.http.get<Client>(url, { headers: headers }),
    );
  }

  async getEntitlementByServiceId(serviceId: string): Promise<Entitlement[]> {
    let url = `${env.base_api_url}/entitlement/service/${serviceId}`;
    let token = await this.userService.getAccessToken();
    let headers = new HttpHeaders().set('Authorization', 'Bearer ' + token);

    return await firstValueFrom(
      this.http.get<Entitlement[]>(url, { headers: headers }),
    );
  }

  async getEntitlementByClientId(clientId: string): Promise<Entitlement[]> {
    let url = `${env.base_api_url}/entitlement/client/${clientId}`;
    let token = await this.userService.getAccessToken();

    let headers = new HttpHeaders().set('Authorization', 'Bearer ' + token);

    return await firstValueFrom(
      this.http.get<Entitlement[]>(url, { headers: headers }),
    );
  }

  async createEntitlements(
    entitlement: EntitlementRequest[],
  ): Promise<boolean> {
    let url = `${env.base_api_url}/entitlement`;
    let response = await firstValueFrom(
      this.http.post<Entitlement[]>(url, entitlement),
    );

    this.storeService.createEntitlementServices(response);

    return true; // CREATED
  }

  async updateEntitlements(
    entitlement: EntitlementRequest[],
  ): Promise<boolean> {
    let url = `${env.base_api_url}/entitlement`;
    let token = await this.userService.getAccessToken();
    let response = await firstValueFrom(
      this.http.put<Entitlement[]>(url, entitlement),
    );

    this.storeService.createEntitlementServices(response);
    return true; // OK
  }

  async deleteEntitlements(
    entitlements: EntitlementRequest[],
  ): Promise<boolean> {
    let url = `${env.base_api_url}/entitlement`;

    let response = await firstValueFrom(
      this.http.request<Entitlement[]>('delete', url, { body: entitlements }),
    );

    this.storeService.deleteEntitlementServices(response);

    return true; // OK
  }

  async addClient(client: Client): Promise<boolean> {
    let url = `${env.base_api_url}/client`;
    let response = await this.http.post(url, client).toPromise();

    return response === 201; // CREATED
  }

  async getServiceSearchKey(
    searchKeyOptions: ServiceSearchKeyOptions,
  ): Promise<ServiceSearchKey[]> {
    let url = `${env.base_api_url}/service/search-key`;
    let token = await this.userService.getAccessToken();
    let headers = new HttpHeaders().set('Authorization', 'Bearer ' + token);

    return await firstValueFrom(
      this.http.post<ServiceSearchKey[]>(url, searchKeyOptions, {
        headers: headers,
      }),
    );
  }
}
